import React, { useEffect, useState } from 'react';
import { BookModel } from '../../../interfaces/book.interface';
import {
  AdminBooksTab,
  AdminDetailsHeader,
  AdminSummaryCard,
  AdminTabs,
  AdminTabDef,
  SummaryField,
} from '../../../components/Admin';
import Loader from '../../../components/Common/Loader';
import { convertTimestampToDateTime } from '../../../helper/helper';
import {
  ProjectDetail,
  ProjectPreOrder,
  ProjectPreOrdersSummary,
  fetchClassroomBookIds,
  getAdminProjectBooks,
  getAdminProjectDetail,
  getAdminProjectPreOrders,
} from './services/Classrooms.service';
import { useBulkMarkPurchased, BulkPurchaseToolbar } from './components/BulkMarkPurchased';
import AdminPreOrdersTab from './components/AdminPreOrdersTab';
import { AdminNavHandler } from '../adminNav';

type TabType = 'books' | 'preorders';

interface ProjectDetailViewProps {
  projectId: string;
  onClose: () => void;
  onOptionClick: AdminNavHandler;
  onOpenClassroom?: (classroomId: string) => void;
}

const formatDate = (timestamp: any): string => {
  if (!timestamp) return '—';
  const date = timestamp._seconds ? new Date(timestamp._seconds * 1000) : new Date(timestamp);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

// startDateTime is stored as a free-form string from the teacher form.
const formatStartDateString = (s: string): string => {
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const ProjectDetailView: React.FC<ProjectDetailViewProps> = ({
  projectId,
  onClose,
  onOptionClick,
  onOpenClassroom,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('books');
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [projectLoader, setProjectLoader] = useState(false);

  const [books, setBooks] = useState<BookModel[]>([]);
  const [booksLoader, setBooksLoader] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [truncated, setTruncated] = useState(false);

  const [preOrders, setPreOrders] = useState<ProjectPreOrder[]>([]);
  const [preOrdersSummary, setPreOrdersSummary] = useState<ProjectPreOrdersSummary>({
    total: 0,
    confirmed: 0,
    pending: 0,
    cancelled: 0,
  });
  const [preOrdersLoader, setPreOrdersLoader] = useState(false);

  const fetchProject = async () => {
    setProjectLoader(true);
    const detail = await getAdminProjectDetail(projectId);
    setProject(detail);
    setProjectLoader(false);
  };

  const fetchPreOrders = async () => {
    setPreOrdersLoader(true);
    const { preOrders: rows, summary } = await getAdminProjectPreOrders(projectId);
    setPreOrders(rows);
    setPreOrdersSummary(summary);
    setPreOrdersLoader(false);
  };

  const fetchBooks = async (p: number) => {
    setBooksLoader(true);
    const result = await getAdminProjectBooks(projectId, p);
    setBooks(result.books as unknown as BookModel[]);
    setTotalPages(result.totalPages);
    setTruncated(result.truncated);
    setBooksLoader(false);
  };

  useEffect(() => {
    fetchProject();
    fetchPreOrders();
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchBooks(page);
  }, [projectId, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = () => {
    fetchProject();
    fetchPreOrders();
    setBooks([]);
    fetchBooks(page);
  };

  // Bulk "mark as purchased" — shared with the Classroom view, scoped to this project.
  const bulk = useBulkMarkPurchased({
    classroomId: project?.classroomId || '',
    teacherEmail: project?.teacherEmail || undefined,
    onCompleted: () => fetchBooks(page),
    getSelectAllIds: async () => {
      if (!project?.classroomId) return [];
      const rows = await fetchClassroomBookIds(project.classroomId, projectId);
      return rows.filter((b) => b.bookId && (b.purchasedCounterValue || 0) <= 0).map((b) => b.bookId);
    },
  });

  if (projectLoader && !project) {
    return (
      <div className="max-w-5xl mx-auto min-h-screen flex justify-center items-center">
        <Loader loader={true} dimentions={'w-8 h-8'} />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="max-w-5xl mx-auto min-h-screen">
        <AdminDetailsHeader title="Project not found" onClose={onClose} />
      </div>
    );
  }

  const tabDef: AdminTabDef<TabType>[] = [
    { key: 'books', label: `Books (${project.bookCount ?? 0})` },
    { key: 'preorders', label: `Pre-orders (${preOrdersSummary.total})` },
  ];

  const studentTotal = project.studentCount || 0;
  const fmtRatio = (n: number) => (studentTotal > 0 ? `${n}/${studentTotal}` : String(n));

  return (
    <div className="max-w-5xl mx-auto min-h-screen">
      <AdminDetailsHeader
        title={<>Project Details — {project.name}</>}
        onClose={onClose}
        onRefresh={handleRefresh}
        refreshing={projectLoader || booksLoader}
      />

      <AdminSummaryCard>
        <SummaryField label="Name" value={project.name} />
        <SummaryField
          label="Status"
          value={
            <span
              className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                project.status === 'completed'
                  ? 'bg-green-100 text-green-700'
                  : project.status === 'in_progress'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {(project.status || 'draft').replace('_', ' ')}
            </span>
          }
        />
        <SummaryField
          label="Classroom"
          value={
            project.classroomName ? (
              <button
                type="button"
                className="text-blue-600 hover:underline text-left"
                onClick={(e) => { e.stopPropagation(); onOpenClassroom?.(project.classroomId); }}
              >
                {project.classroomName}
              </button>
            ) : (
              project.classroomId
            )
          }
        />
        <SummaryField
          label="Teacher"
          value={
            project.teacherEmail ? (
              <button
                type="button"
                className="text-blue-600 hover:underline text-left"
                onClick={(e) => {
                  e.stopPropagation();
                  onOptionClick('Customers', { teacherEmail: project.teacherEmail || undefined });
                }}
              >
                {project.teacherEmail}
              </button>
            ) : (
              '—'
            )
          }
        />
        <SummaryField
          label="Start Date"
          value={
            project.startDateTime
              ? formatStartDateString(project.startDateTime)
              : project.startDate
              ? formatDate(project.startDate)
              : '—'
          }
        />
        <SummaryField label="Template" value={project.templateName || '—'} />
        <SummaryField label="Default" value={project.isDefaultProject ? 'Yes' : 'No'} />
        <SummaryField label="Students" value={project.studentCount} />
        <SummaryField label="Books" value={project.bookCount} />
        <SummaryField label="Students with a book" value={fmtRatio(project.finishedStudentCount)} />
        <SummaryField label="Parent flyer responses" value={fmtRatio(project.parentResponseCount)} />
        <SummaryField label="Students with orders" value={fmtRatio(project.orderedStudentCount)} />
        <SummaryField label="Created" value={project.createdAt ? convertTimestampToDateTime(project.createdAt) : '—'} />
      </AdminSummaryCard>

      <AdminTabs<TabType> tabs={tabDef} activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === 'books' && (
        <div>
          {truncated && (
            <div className="mb-3 text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded px-2 py-1">
              Showing the first 1,000 books for this project.
            </div>
          )}
          <BulkPurchaseToolbar bulk={bulk} selectAllLabel="Select all (project)" />
          {booksLoader ? (
            <div className="flex justify-center py-8">
              <Loader loader={booksLoader} dimentions={'w-8 h-8'} />
            </div>
          ) : (
            <AdminBooksTab
              books={books}
              currentPage={page}
              totalBooksPages={totalPages}
              handleBooksPageChange={(p) => setPage(+p)}
              selectable={bulk.selectMode}
              selectedBookIds={bulk.selectedIds}
              onToggleSelect={bulk.toggleSelect}
            />
          )}
        </div>
      )}

      {activeTab === 'preorders' && (
        <div>
          {preOrdersLoader ? (
            <div className="flex justify-center py-8">
              <Loader loader={preOrdersLoader} dimentions={'w-8 h-8'} />
            </div>
          ) : (
            <AdminPreOrdersTab preOrders={preOrders} summary={preOrdersSummary} />
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectDetailView;
